import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { AdminOnboardingListItem } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { useState } from 'react'
import { useAdminOnboardingQuery } from '@/hooks/api'
import { CellLink } from '@/utils/data-table'
import { formatFullDate } from '@/utils/format'

type SearchParams = { page?: number; search?: string }

export const Route = createFileRoute('/_protected/admin/onboarding/')({
  component: AdminOnboardingPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

const LIMIT = 20

const columns: TableColumn<AdminOnboardingListItem>[] = [
  {
    header: 'ID',
    width: '100px',
    render: (item) => (
      <CellLink to="/admin/onboarding/$id" params={{ id: item.id }} className="font-mono text-xs">
        {item.id.slice(0, 8)}
      </CellLink>
    ),
  },
  {
    header: 'User',
    render: (item) => (
      <CellLink to="/admin/users/$id" params={{ id: item.userId }}>
        {item.userName || item.userEmail}
      </CellLink>
    ),
  },
  {
    header: 'Role',
    width: '100px',
    render: (item) => (
      <Badge variant="secondary" size="sm">
        {item.userType ?? 'Not set'}
      </Badge>
    ),
  },
  {
    header: 'Status',
    width: '120px',
    render: (item) => {
      const variantMap = {
        completed: 'success',
        in_progress: 'warning',
        abandoned: 'secondary',
      } as const
      const labelMap = {
        completed: 'Completed',
        in_progress: 'In Progress',
        abandoned: 'Abandoned',
      } as const
      return (
        <Badge variant={variantMap[item.status]} size="sm">
          {labelMap[item.status]}
        </Badge>
      )
    },
  },
  {
    header: 'Messages',
    width: '90px',
    render: (item) => <CellText>{item.messageCount}</CellText>,
  },
  {
    header: 'Started',
    width: '130px',
    render: (item) => <CellText>{formatFullDate(item.createdAt)}</CellText>,
  },
]

function AdminOnboardingPage() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const onboardingQuery = useAdminOnboardingQuery({
    query: { page, limit: LIMIT, search: search || undefined },
  })

  const items = onboardingQuery.data?.data?.items ?? []
  const total = onboardingQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-6 py-6">
      <SearchInput
        placeholder="Search by user name or email..."
        value={searchInput}
        onChange={setSearchInput}
        onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
      />
      <DataTable
        columns={columns}
        data={items}
        isLoading={onboardingQuery.isLoading}
        emptyMessage="No onboarding conversations found."
        keyExtractor={(item) => item.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
